import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  ButtonBase,
  IconButton,
  InputBase,
  ListItemIcon,
  Menu,
  MenuItem,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

import emojis from '../../../assets/emojis.json';

export interface ChatMessageAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}

export interface ChatMenuAnchor {
  x: number;
  y: number;
  // Opens straight on the full emoji list.
  pickEmoji?: boolean;
}

interface Props {
  anchor: ChatMenuAnchor | null;
  onClose: () => void;
  // Without it there is no reaction row.
  onReact?: (emoji: string) => void;
  actions: ChatMessageAction[];
}

// One menu for a message, the same in every chat: quick reactions on top
// (more on demand), then what can be done with it. Opened by right click or
// from the buttons beside the bubble.
export const ChatMessageMenu: React.FC<Props> = ({ anchor, onClose, onReact, actions }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setExpanded(!!anchor?.pickEmoji);
    setSearch('');
  }, [anchor]);

  const found = useMemo(
    () => emojis.other.filter(emoji => emoji.name.toLowerCase().includes(search.trim().toLowerCase())),
    [search]
  );

  const run = (action: () => void) => {
    onClose();
    action();
  };

  return (
    <Menu
      open={!!anchor}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchor ? { top: anchor.y, left: anchor.x } : undefined}
      classes={{ paper: classes.paper }}
      MenuListProps={{ dense: true }}
    >
      {onReact && (
        <Box className={classes.reactions} onKeyDown={event => event.stopPropagation()}>
          <Box display="flex" alignItems="center">
            {emojis.favorite.map(emoji => (
              <ButtonBase
                key={emoji.emoji}
                className={classes.emoji}
                onClick={() => run(() => onReact(emoji.emoji))}
                aria-label={`React with ${emoji.name}`}
              >
                {emoji.emoji}
              </ButtonBase>
            ))}
            <IconButton
              size="small"
              onClick={() => setExpanded(value => !value)}
              aria-label={expanded ? 'Fewer reactions' : 'More reactions'}
            >
              {expanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
            </IconButton>
          </Box>
          {expanded && (
            <>
              <InputBase
                autoFocus
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search emoji"
                className={classes.search}
                inputProps={{ 'aria-label': 'Search emoji' }}
              />
              <Box className={classes.grid}>
                {found.map(emoji => (
                  <ButtonBase
                    key={emoji.emoji}
                    className={classes.emoji}
                    onClick={() => run(() => onReact(emoji.emoji))}
                    aria-label={`React with ${emoji.name}`}
                  >
                    {emoji.emoji}
                  </ButtonBase>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
      {actions.map(action => (
        <MenuItem
          key={action.key}
          onClick={() => run(action.onClick)}
          className={action.danger ? classes.danger : undefined}
        >
          <ListItemIcon className={classes.icon}>{action.icon}</ListItemIcon>
          {action.label}
        </MenuItem>
      ))}
    </Menu>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: { minWidth: 220, borderRadius: 14 },
    reactions: {
      padding: '2px 6px 6px',
      marginBottom: 4,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    emoji: {
      width: 34,
      height: 34,
      borderRadius: '50%',
      fontSize: 20,
      transition: 'transform 120ms',
      '&:hover': { transform: 'scale(1.2)', backgroundColor: theme.palette.action.hover },
    },
    search: {
      width: '100%',
      margin: '6px 0 4px',
      padding: '2px 10px',
      borderRadius: 16,
      fontSize: 14,
      backgroundColor: theme.palette.action.hover,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 34px)',
      maxHeight: 180,
      overflowY: 'auto',
    },
    icon: { minWidth: 30, color: theme.palette.text.secondary },
    danger: { color: theme.palette.error.main, '& $icon': { color: theme.palette.error.main } },
  })
);
